// EL MENSAJE DEL KIT DE PROSPECTO Y EL DE RENOVACIÓN SON DOS MENSAJES.
//
// Lissette, 2026-10-12 (urgente): *"me están diciendo que cuando cambian los
// mensajes del kit de bienvenida de prospecto se cambia en renovación, y son
// diferentes"*.
//
// Los dos editores (Prospecto y Referido → "Mensajes del Kit por tipo", y
// Renovaciones → "Kit de bienvenida") guardaban en el MISMO mapa,
// `AgencySetting.welcomeKitTemplates`, con la MISMA llave: el tipo de póliza a
// secas ("aca", "life"…). Escribir uno era escribir el otro.
//
// Ahora cada pantalla tiene su llave: `prospecto__aca`, `renovacion__aca`. La
// llave vieja (`aca`) se sigue LEYENDO como respaldo, para que ningún mensaje
// ya escrito deje de salir el día del cambio. Pero ya no se escribe: en cuanto
// una pantalla guarda, guarda lo suyo, y lo de la otra no se mueve.
//
// El servidor elige el mensaje con la misma regla (resolveKitTemplate en
// deskte-api/src/routes/policies.routes.ts); una prueba compara las dos.
//
// Las llaves del kit de cambio de vida (`life_change`, `life_change__<tipo>`)
// no pasan por aquí: no son de un tipo de póliza y nunca chocaron.

export const KIT_PROSPECTO = 'prospecto';
export const KIT_RENOVACION = 'renovacion';
export const CONTEXTOS_DE_KIT = [KIT_PROSPECTO, KIT_RENOVACION];

/** La llave de un mensaje de kit: `prospecto__aca`, `renovacion__life`. */
export const claveDeKitPorContexto = (contexto, tipo) => `${contexto}__${tipo}`;

/** ¿Tiene asunto o cuerpo escrito? Un mensaje vacío no cuenta como mensaje. */
export const tieneMensaje = (t) => !!(t && typeof t === 'object'
  && ((t.subject && String(t.subject).trim()) || (t.body && String(t.body).trim())));

// ── LA VERSIÓN EN INGLÉS (Josef, 2026-10-27) ─────────────────────────────
//
// *"El kit de Medicare Advantage no sale en inglés a la persona que habla
// inglés; no reconoce el idioma del cliente."* Y era así: cada mensaje del kit
// era UN texto, en el idioma en que la agencia lo escribiera, y salía igual
// para todos. Ahora cada mensaje puede tener además su versión en inglés,
// guardada al lado con `__en` (`prospecto__ma__en`). Al enviar a un cliente
// cuyo idioma es inglés se usa esa versión si existe; si no, el mensaje de
// siempre — un tipo que solo tiene el principal se comporta exactamente como
// antes. La misma regla en el servidor: elegirMensajeDeKit (policies.routes.ts).
export const IDIOMA_INGLES = 'en';
export const sufijoDeIdioma = (idioma) => (idioma === IDIOMA_INGLES ? '__en' : '');

/** El idioma del cliente para el kit: 'en' o 'es'. Acepta las dos formas de la ficha. */
export function idiomaDelClienteParaKit(c) {
  const v = String((c && (c.preferredLanguage || c.preferredLang)) || '').toLowerCase();
  return v.startsWith('en') ? IDIOMA_INGLES : 'es';
}

/** Solo la versión en inglés de este tipo en esta pantalla (null si no hay). */
export function mensajeDeKitEnIngles(mapa, contexto, tipo) {
  const m = mapa && typeof mapa === 'object' ? mapa : {};
  const t = m[claveDeKitPorContexto(contexto, tipo) + sufijoDeIdioma(IDIOMA_INGLES)];
  return tieneMensaje(t) ? t : null;
}

/**
 * El mensaje que se usa para este tipo en esta pantalla: si el cliente habla
 * inglés y hay versión en inglés, esa; si no, el suyo; si no tiene, el de antes
 * (la llave sin pantalla, de cuando las dos compartían). null si ninguno:
 * entonces ese tipo NO envía kit.
 */
export function mensajeDeKit(mapa, contexto, tipo, idioma) {
  const m = mapa && typeof mapa === 'object' ? mapa : {};
  if (idioma === IDIOMA_INGLES) {
    const enIngles = mensajeDeKitEnIngles(m, contexto, tipo);
    if (enIngles) return enIngles;
  }
  const propio = m[claveDeKitPorContexto(contexto, tipo)];
  if (tieneMensaje(propio)) return propio;
  const deAntes = m[tipo];
  if (tieneMensaje(deAntes)) return deAntes;
  return null;
}

/**
 * El mapa con el mensaje de ESTA pantalla y este tipo cambiado. No toca nada
 * más: ni la llave de antes ni la de la otra pantalla. Vacío = se borra el
 * propio, y vuelve a usarse el de antes (si lo hay; si no, no hay kit).
 */
export function mapaConMensaje(mapa, contexto, tipo, valor, idioma) {
  const siguiente = { ...(mapa && typeof mapa === 'object' ? mapa : {}) };
  // Con idioma 'en' se escribe la versión en inglés; el principal no se toca.
  const clave = claveDeKitPorContexto(contexto, tipo) + sufijoDeIdioma(idioma);
  if (tieneMensaje(valor)) siguiente[clave] = { subject: valor.subject || '', body: valor.body || '' };
  else delete siguiente[clave];
  return siguiente;
}

/** Las llaves del kit de cambio de vida, que se guardan tal cual. */
export const esLlaveDeCambioDeVida = (k) => k === 'life_change' || String(k || '').startsWith('life_change__');

// YA NO HAY "POR DEFECTO" (Lissette 2026-10-23: *"la gente tiene mucha
// confusión con el predeterminado… quita el predeterminado para todos y deja
// solo los personalizados; si una persona no tiene el mensaje personalizado
// para el tipo de póliza, no se envíe"*). Antes había un "⭐ Por defecto" de
// Renovaciones (`renewalKitTemplate`) y otro de Prospecto
// (`prospecto____default__`). Siguen guardados —no se borró nada—, pero ni se
// ven ni se usan: un tipo sin mensaje propio (ni de antes) NO envía kit.
